<script setup lang="ts">
import { watch } from 'vue'
import lodash from 'lodash'
import useSimulationStore from '~/store/simulation'
import useUserStore from '~/store/user'
import type { SimulationPoolConnector } from '#cmui/types/simulation'

const simulationStore = useSimulationStore()
const userStore = useUserStore()

const LEGACY_URL = useRuntimeConfig().public.LEGACY_URL as string
const { locale } = useI18n()
const { debounce } = lodash

const emits = defineEmits(['showVehicleSelection', 'showConnectorSelection'])

const redirectToInfo = () => {
    const supportUrl = `https://support.chargemap.com/l/${locale.value}/article/hx9axz9sh6`

    if (window.location !== window?.parent?.location) {
        ;(window.top as Window).postMessage(
            { action: 'openAbsoluteUrl', url: supportUrl },
            document.referrer
        )
    } else {
        const newWnd = window.open(supportUrl, '_blank')

        if (newWnd) {
            newWnd.opener = null
        }
    }
}

const handleSelectVehicle = () => {
    simulationStore.meta.isLoading = true
    emits('showVehicleSelection')
}

const debouncedFetchPriceDetails = debounce(() => {
    simulationStore.fetchSimulationPriceDetails()
}, 800)

watch(
    () => simulationStore.simulator.chargeTime,
    () => {
        if (
            simulationStore.simulator.chargeTime &&
            simulationStore.simulator.chargeTime.length === 5
        ) {
            debouncedFetchPriceDetails()
        }
    }
)
</script>

<template>
    <div
        class="min-h-screen flex flex-col justify-start items-center gap-6 py-8 px-6 bg-white"
    >
        <h2 class="font-bold text-gray-l-700 text-2xl m-0">
            {{ $t('simulation.charge.results.title') }}
        </h2>
        <SimulationUserSettings
            v-if="
                simulationStore.currentConnector &&
                simulationStore.simulator.currentVehicle
            "
            v-model="simulationStore.simulator.chargeTime"
            class="w-full"
            :connector="
                simulationStore.currentConnector as SimulationPoolConnector
            "
            :vehicle="simulationStore.simulator.currentVehicle"
            :legacy-api-url="LEGACY_URL"
            :has-time-input="simulationStore.hasPriceDuration"
            @selectConnector="$emit('showConnectorSelection')"
            @selectVehicle="handleSelectVehicle()"
        />
        <SimulationBatteryLevel
            v-model="simulationStore.simulator.vehicleBatteryLevels"
            class="w-full"
            :is-disabled="simulationStore.meta.isLoading"
            @change="simulationStore.fetchSimulationPriceDetails"
        />
        <ChargemapSkeletonLoader
            v-if="simulationStore.meta.isLoading"
            class="w-full h-[100px] 425:h-[126px] rounded-lg 425:rounded-2xl mt-9 425:mt-10"
        />
        <SimulationResultsReducedDisplay
            v-else-if="simulationStore.priceDetails"
            class="w-full"
            :price-details="simulationStore.priceDetails"
            :is-boost="userStore.isBoostUser"
            :is-disabled="simulationStore.meta.isError"
        />
        <SimulationPriceStructure
            v-if="simulationStore.priceDetails"
            class="w-full"
            :price-details="simulationStore.priceDetails"
            :is-free="simulationStore.isFreeCharge"
            :is-boost="userStore.isBoostUser"
            :is-loading="
                simulationStore.meta.isLoading || simulationStore.meta.isError
            "
        />
        <ChargemapMessageTall @actionClick="redirectToInfo()">
            <template #icon>
                <IconInfoCircle />
            </template>
            <span>
                {{ $t('simulation.charge.results.estimation_notice.message') }}
            </span>
            <template #action>
                <span class="flex items-center">
                    {{
                        $t('simulation.charge.results.estimation_notice.action')
                    }}
                </span>
            </template>
        </ChargemapMessageTall>
    </div>
</template>
